import { directorSkillSelectionFromState, type DirectorSkillSelection } from '../components/chat/directorSkillSelection'
import { useState, useEffect, useRef } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import EmailLoginForm from '../components/auth/EmailLoginForm'
import LandingPageV2 from './LandingPageV2'
import TermsAcknowledgment from '../components/auth/TermsAcknowledgment'
import { APP_CONSOLE_NAME } from '../constants/brand'
import { createDebug } from '../utils/debugLog'
import { mapAuthError } from '../components/auth/mapAuthError'
import { SUPPORTED_LOCALE_OPTIONS } from '../i18n/supportedLocales'
import {
  projectPlanDraftFromUnknown,
  type ProjectPlanDraft,
} from '../components/landing-v2/ProjectPlanModal'
import { trackInteraction } from '../analytics/interactionAnalytics'

const log = createDebug('dw:i18n:preauth-selector')

type LoginLocationState = {
  selectedSkill?: DirectorSkillSelection
  returnTo?: string
  prompt?: string
  style?: string
  productionTarget?: string
  aspectRatio?: string
  // v1 upfront project plan captured before the /login redirect. Parsed here so
  // the field-agnostic forward spread (`const { returnTo, ...forwardState }`)
  // carries it back to the landing resume without dropping it (GAP-1b).
  planDraft?: ProjectPlanDraft
}

function stringField(value: object, key: string): string | undefined {
  const entry = Object.entries(value).find(([entryKey]) => entryKey === key)
  return typeof entry?.[1] === 'string' ? entry[1] : undefined
}

function objectField(value: object, key: string): object | undefined {
  const entry = Object.entries(value).find(([entryKey]) => entryKey === key)
  const field = entry?.[1]
  return field && typeof field === 'object' ? field : undefined
}

export function loginLocationState(value: object | null | undefined): LoginLocationState | null {
  if (!value || typeof value !== 'object') {
    return null
  }

  return {
    returnTo: stringField(value, 'returnTo'),
    prompt: stringField(value, 'prompt'),
    selectedSkill: directorSkillSelectionFromState(value, 'selectedSkill'),
    style: stringField(value, 'style'),
    productionTarget: stringField(value, 'productionTarget'),
    aspectRatio: stringField(value, 'aspectRatio'),
    planDraft: projectPlanDraftFromUnknown(objectField(value, 'planDraft')) ?? undefined,
  }
}

export default function LoginPage() {
  const { isAuthenticated, isLoading, signInWithGoogle, signInWithFacebook } = useAuth()
  const { t, i18n } = useTranslation(['login', 'auth'])
  const location = useLocation()
  const navigate = useNavigate()
  const [signingInWith, setSigningInWith] = useState<'google' | 'facebook' | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [showOAuth, setShowOAuth] = useState(false)
  // Explicit ToS consent for the OAuth buttons. OAuth can create an account and
  // cannot distinguish new from returning users, so it gets the same gate as
  // email sign-up. Unticked by default.
  const [oauthTermsAccepted, setOauthTermsAccepted] = useState(false)
  const backdropRef = useRef<HTMLDivElement>(null)

  const locationState = loginLocationState(location.state)

  // Lock body scroll while the login modal is open so the landing page behind
  // it can't scroll. Restored on unmount / navigation away.
  useEffect(() => {
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previous
    }
  }, [])

  useEffect(() => {
    if (backdropRef.current) backdropRef.current.inert = true
  }, [])

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="flex flex-col items-center gap-4">
          <svg className="animate-spin h-8 w-8 text-[#8dd5d8]" viewBox="0 0 24 24">
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
              fill="none"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <span className="font-[Fustat] text-white/60">{t('loading')}</span>
        </div>
      </div>
    )
  }

  if (isAuthenticated) {
    const returnTo = locationState?.returnTo || '/'
    const { returnTo: _r, ...forwardState } = locationState || {}
    return <Navigate to={returnTo} state={forwardState} replace />
  }

  const handleGoogleSignIn = async () => {
    // Both buttons are disabled while unticked; this is the backstop.
    if (!oauthTermsAccepted) return
    setError(null)
    trackInteraction('auth_signin_submitted', { method: 'google' })
    setSigningInWith('google')
    try {
      await signInWithGoogle()
    } catch (err) {
      setError(mapAuthError(t, 'oauth', err instanceof Object ? err : {}, { provider: 'google' }))
      setSigningInWith(null)
    }
  }

  const handleFacebookSignIn = async () => {
    if (!oauthTermsAccepted) return
    setError(null)
    trackInteraction('auth_signin_submitted', { method: 'facebook' })
    setSigningInWith('facebook')
    try {
      await signInWithFacebook()
    } catch (err) {
      setError(mapAuthError(t, 'oauth', err instanceof Object ? err : {}, { provider: 'facebook' }))
      setSigningInWith(null)
    }
  }

  // Close the login overlay — return to where the user came from, or the
  // landing page as a fallback (e.g. deep-linked directly to /login).
  const handleClose = () => {
    const returnTo = locationState?.returnTo || '/'
    navigate(returnTo, { replace: true })
  }

  return (
    <>
      {/* Landing page renders behind the overlay so the blurred backdrop shows
          the real page rather than flat black. It is inert (aria-hidden +
          pointer-events-none) while the login modal is open. */}
      <div ref={backdropRef} aria-hidden="true" className="pointer-events-none">
        <LandingPageV2 heroVideoEnabled={false} />
      </div>

      {/* Blurred modal overlay */}
      <div
        className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 backdrop-blur-md px-6"
        onClick={handleClose}
      >
        <div
          className="relative max-h-[calc(100vh-48px)] w-full max-w-md overflow-y-auto rounded-[24px] border border-white/10 bg-[#0f0f0f] p-6 shadow-[0_40px_80px_rgba(0,0,0,0.5)] sm:p-8"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close button */}
          <button
            onClick={handleClose}
            aria-label={t('auth:closeLabel')}
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full text-white/50 hover:text-white hover:bg-white/10 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        <div className="flex flex-col items-center mb-6">
          <img
            src="/landing-v2/logo-icon.png"
            alt={APP_CONSOLE_NAME}
            className="w-8 h-8 object-contain mb-3"
          />
          <h1 className="font-[Fustat] font-semibold text-display-sm text-white leading-tight text-center">
            {APP_CONSOLE_NAME}
          </h1>
          <p className="font-[Fustat] text-lg font-medium text-[#a1a1aa] mt-1.5 text-center">
            {t('signInWithEmail')}
          </p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-500/15 border border-red-500/40 rounded-xl text-red-400 text-base font-[Fustat]">
            {error}
          </div>
        )}

        <EmailLoginForm />

        <div className="flex items-center my-6">
          <div className="flex-1 h-px bg-white/10" />
          <span className="px-3 text-base text-white/40 font-[Fustat]">{t('or')}</span>
          <div className="flex-1 h-px bg-white/10" />
        </div>

        {showOAuth ? (
          <div className="space-y-3">
            {/* RQ16043652: acknowledgment shown immediately above the OAuth
                account-creation actions so it stays co-visible on every viewport. */}
            <TermsAcknowledgment
              context="oauth"
              accepted={oauthTermsAccepted}
              onAcceptedChange={setOauthTermsAccepted}
            />

            <button
              onClick={handleGoogleSignIn}
              disabled={signingInWith !== null || !oauthTermsAccepted}
              className="w-full flex items-center justify-center gap-2 h-12 rounded-[40px] bg-white/[0.06] border border-white/10 text-white font-[Fustat] font-medium text-lg hover:bg-white/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path
                  fill="currentColor"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="currentColor"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="currentColor"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="currentColor"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              {signingInWith === 'google' ? t('signingIn') : t('signInWithGoogle')}
            </button>

            <button
              onClick={handleFacebookSignIn}
              disabled={signingInWith !== null || !oauthTermsAccepted}
              className="w-full flex items-center justify-center gap-2 h-12 rounded-[40px] text-white font-[Fustat] font-medium text-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: '#1877F2' }}
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
              </svg>
              {signingInWith === 'facebook' ? t('signingIn') : t('signInWithFacebook')}
            </button>
          </div>
        ) : (
          <button
            onClick={() => setShowOAuth(true)}
            className="w-full text-lg font-medium text-white/40 hover:text-white/70 transition-colors font-[Fustat]"
          >
            {t('moreSignInOptions')}
          </button>
        )}
          {/* Pre-auth language selector */}
          <div className="mt-6 flex justify-center">
            <select
              data-testid="preauth-language-select"
              aria-label={t('selectLanguage')}
              value={i18n.language}
              onChange={(e) => {
                const lng = e.target.value
                log({ event: 'preauth-language-change', from: i18n.language, to: lng })
                i18n.changeLanguage(lng)
              }}
              className="appearance-none bg-white/[0.04] border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white/60 font-[Fustat] cursor-pointer hover:bg-white/[0.08] transition-colors focus:outline-none focus:border-[#8dd5d8]/60"
            >
              {SUPPORTED_LOCALE_OPTIONS.map(({ code, label }) => (
                <option key={code} value={code} className="bg-[#0f0f0f] text-white">
                  {label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </>
  )
}
